import React, { useState, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import * as yup from 'yup';
import FormControl from '@mui/material/FormControl';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ClearIcon from '@mui/icons-material/Clear';
import { useFormik } from 'formik';
import { styled } from '@mui/material/styles';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

const CreateStage = (props) => {
  const { is_stage_modal_open,
    handleStageModal,
    is_loading,
    handleStageSubmit,
    handleStageUpdate,
    c_action,
    stage_id,
    selected_data,
    sidebar_menu_list,
  } = props;
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [previewImage, setPreviewImage] = useState(null);
  const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
  });

  const handleStageCloseModalChange = () => {
    formik.resetForm();
    setPreviewImage(null);
    handleStageModal(false);
  };

  const validationSchema = yup.object().shape({
    stage_name: yup.string().trim().required('Stage Name is required.'),
    color: yup.string().trim().required('Stage Color is required.'),
    display_order: yup.number().required('Display Order is required.'),
    sidebar_menu_id: yup.number().required('Sidebar Menu Name is required.'),
  });

  const onSubmit = async (values) => {
    let res_create, res_update;

    if (c_action === "UPDATE") {
      const payload = { stage_id, updated_data: values };
      res_update = await handleStageUpdate(payload);
    } else {
      res_create = await handleStageSubmit(values);
    }

    if (
      (res_create || res_update) &&
      !(res_create?.error || res_update?.error)
    ) {
      formik.resetForm();
      setPreviewImage(null);
      handleStageModal(false);
      window.location.reload()
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      stage_name: c_action === "UPDATE" ? selected_data.name : '',
      color: c_action === "UPDATE" ? selected_data.color : '',
      display_order: c_action === "UPDATE" ? selected_data.display_order : '',
      sidebar_menu_id: c_action === "UPDATE" ? selected_data.sidebar_menu_id : '',
      stage_logo: null,
    },
    validationSchema: validationSchema,
    onSubmit,
  });

  useEffect(() => {
    if (formik.values.stage_logo) {
      const fileReader = new FileReader();
      fileReader.onload = () => setPreviewImage(fileReader.result);
      fileReader.readAsDataURL(formik.values.stage_logo);
    } else {
      setPreviewImage(null);
    }
  }, [formik.values.stage_logo]);

  return (
    <Dialog
      fullScreen={fullScreen}
      open={is_stage_modal_open}
      onClose={handleStageCloseModalChange}
      aria-labelledby="responsive-dialog-title"
      component="form"
      onSubmit={formik.handleSubmit}
      sx={{
        '& .MuiDialog-paper': {
          weight: '300px'
        },
      }}
    >
      <DialogTitle
        id="responsive-dialog-title"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {(c_action === 'UPDATE' && 'Update Stage') || (c_action === 'CREATE' && 'Create Stage')}
        <IconButton size="medium" onClick={handleStageCloseModalChange}>
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          '& .MuiTextField-root,.MuiFormControl-root': {
            width: { md: '30ch', xs: '100%' },
            fontSize: '14px',
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: '6px' }}>
          <TextField
            label="Stage Name"
            variant="outlined"
            size="small"
            name="stage_name"
            value={formik.values.stage_name}
            onChange={formik.handleChange('stage_name')}
            onBlur={formik.handleBlur('stage_name')}
            error={formik.touched.stage_name && Boolean(formik.errors.stage_name)}
            helperText={formik.touched.stage_name && formik.errors.stage_name}
            sx={{
              fontSize: { xs: '12px', sm: '14px' },
            }}
          />

          <TextField
            label="Stage Color"
            variant="outlined"
            size="small"
            name="color"
            value={formik.values.color}
            onChange={formik.handleChange('color')}
            onBlur={formik.handleBlur('color')}
            error={formik.touched.color && Boolean(formik.errors.color)}
            helperText={formik.touched.color && formik.errors.color}
            sx={{
              fontSize: { xs: '12px', sm: '14px' },
            }}
          />

          <FormControl size="small" fullWidth error={formik.touched.sidebar_menu_id && Boolean(formik.errors.sidebar_menu_id)}>
            <InputLabel id="sidebar-menu-select-label">Sidebar Menu Name</InputLabel>
            <Select
              labelId="sidebar-menu-select-label"
              id="sidebar_menu_id"
              name="sidebar_menu_id"
              value={formik.values.sidebar_menu_id}
              label="Sidebar Menu Name"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              {sidebar_menu_list && sidebar_menu_list.map((menu) => (
                <MenuItem key={menu.id} value={menu.id}>
                  {menu.side_name}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.sidebar_menu_id && formik.errors.sidebar_menu_id && (
              <Typography variant="caption" color="error" sx={{ mx: 1.5, mt: 0.5 }}>
                {formik.errors.sidebar_menu_id}
              </Typography>
            )}
          </FormControl>

          <TextField
            label="Display Order"
            variant="outlined"
            size="small"
            type="number"
            name="display_order"
            value={formik.values.display_order}
            onChange={formik.handleChange('display_order')}
            onBlur={formik.handleBlur('display_order')}
            error={formik.touched.display_order && Boolean(formik.errors.display_order)}
            helperText={formik.touched.display_order && formik.errors.display_order}
            sx={{
              fontSize: { xs: '12px', sm: '14px' },
            }}
          />

          <FormControl error={formik.touched.stage_logo && Boolean(formik.errors.stage_logo)} fullWidth>
            <Button
              component="label"
              disableElevation
              variant="contained"
              startIcon={<CloudUploadIcon />}
              sx={{ fontSize: { xs: '12px', sm: '14px' } }}
            >
              {is_loading && formik.values.stage_logo ? 'Uploading...' : 'stage_logo'}
              <VisuallyHiddenInput
                type="file"
                accept="image/*"
                onChange={(event) => {
                  const file = event.target.files[0];
                  formik.setFieldValue('stage_logo', file);
                }}
              />
            </Button>

            {previewImage && (
              <Box
                sx={{
                  marginTop: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <img
                  src={previewImage}
                  alt="Preview"
                  style={{
                    width: '50px',
                    height: '50px',
                    objectFit: 'contain',
                    borderRadius: '1px',
                    border: '1px solid #ddd',
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => formik.setFieldValue('stage_logo', null)}
                  sx={{ fontSize: '18px' }}
                >
                  <ClearIcon />
                </IconButton>
              </Box>
            )}

            {!previewImage && c_action === "UPDATE" && selected_data.stage_logo && (
              <Box sx={{ marginTop: 2, textAlign: 'center' }}>
                <img
                  src={selected_data.stage_logo}
                  alt="Current Stage Logo"
                  style={{
                    width: '50px',
                    height: '50px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                  }}
                />
                <Typography sx={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                  Current Logo
                </Typography>
              </Box>
            )}
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          padding: { xs: '12px', sm: '8px 16px 24px 24px' },
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          width: { md: '30ch', xs: '100%' }
        }}
      >
        <Button
          variant="outlined"
          color="error"
          onClick={handleStageCloseModalChange}
          sx={{
            fontSize: { xs: '12px', sm: '14px' },
            borderColor: "#ff6100", color: "#4c4e52"
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          type="submit"
          sx={{
            fontSize: { xs: '12px', sm: '14px' },
            backgroundColor: "#ff6100"
          }}
        >
          {(c_action === 'UPDATE' && 'Update') || (c_action === 'CREATE' && 'Submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateStage;